import { firstGraphQLError } from 'utils/apolloGraphQLError';

import { SaleDocuments } from 'query/__generated__/saleDataRoom';

import { documentTypeLabel } from './saleDocumentTypes';

export type SaleDataRoom = SaleDocuments['saleDocuments'];
export type SaleDataRoomDocument = SaleDataRoom['documents'][number];

type SaleDataRoomSaleKey = SaleDataRoom['sale_keys'][number];
type SaleDataRoomPool = SaleDataRoomSaleKey['pools'][number];

export function countPoolDocuments(pool: SaleDataRoomPool): number {
  return pool.assets.reduce(
    (total, asset) => total + asset.documents.length,
    pool.documents.length,
  );
}

export function countSaleKeyDocuments(saleKey: SaleDataRoomSaleKey): number {
  return saleKey.pools.reduce((total, pool) => total + countPoolDocuments(pool), 0);
}

export function countDataRoomDocuments(dataRoom: SaleDataRoom): number {
  return dataRoom.sale_keys.reduce(
    (total, saleKey) => total + countSaleKeyDocuments(saleKey),
    dataRoom.documents.length,
  );
}

// Unlike a search, this keeps no folder for its name: a folder stays only while it holds a
// document that passes.
export function filterDataRoomDocuments(
  dataRoom: SaleDataRoom,
  keep: (document: SaleDataRoomDocument) => boolean,
): SaleDataRoom {
  const saleKeys = dataRoom.sale_keys.flatMap((saleKey) => {
    const pools = saleKey.pools.flatMap((pool) => {
      const assets = pool.assets.flatMap((asset) => {
        const documents = asset.documents.filter(keep);
        return documents.length > 0 ? [{ ...asset, documents }] : [];
      });
      const documents = pool.documents.filter(keep);
      return documents.length > 0 || assets.length > 0 ? [{ ...pool, documents, assets }] : [];
    });
    return pools.length > 0 ? [{ ...saleKey, pools }] : [];
  });
  return { ...dataRoom, documents: dataRoom.documents.filter(keep), sale_keys: saleKeys };
}

// Filters only the tree the caller was given, so it can never surface a document that view_as
// already withheld. A folder whose own name matches keeps everything under it; any other folder is
// kept only for its matching descendants, and dropped when it has none.
export function filterDataRoom(dataRoom: SaleDataRoom, query: string): SaleDataRoom {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return dataRoom;
  }
  const matches = (...values: string[]) =>
    values.some((value) => value.toLowerCase().includes(needle));
  const matchingDocuments = (documents: SaleDataRoomDocument[]) =>
    documents.filter((document) =>
      matches(document.file_name, document.doc_type, documentTypeLabel(document.doc_type)),
    );

  const saleKeys = dataRoom.sale_keys.flatMap((saleKey) => {
    if (matches(saleKey.folder_name, saleKey.sale_key)) {
      return [saleKey];
    }
    const pools = saleKey.pools.flatMap((pool) => {
      if (matches(pool.folder_name)) {
        return [pool];
      }
      const assets = pool.assets.flatMap((asset) => {
        if (matches(asset.folder_name, asset.loan_id)) {
          return [asset];
        }
        const documents = matchingDocuments(asset.documents);
        return documents.length > 0 ? [{ ...asset, documents }] : [];
      });
      const documents = matchingDocuments(pool.documents);
      return documents.length > 0 || assets.length > 0 ? [{ ...pool, documents, assets }] : [];
    });
    return pools.length > 0 ? [{ ...saleKey, pools }] : [];
  });

  return { ...dataRoom, documents: matchingDocuments(dataRoom.documents), sale_keys: saleKeys };
}

// The pool page's data room: that pool's folder alone, under its sale key, with none of the sale's
// own documents or its other pools. Like the filters, it narrows only what api already returned.
export function scopeDataRoomToPool(dataRoom: SaleDataRoom, offeringId: string): SaleDataRoom {
  const saleKeys = dataRoom.sale_keys.flatMap((saleKey) => {
    const pools = saleKey.pools.filter((pool) => pool.offering_id === offeringId);
    return pools.length > 0 ? [{ ...saleKey, pools }] : [];
  });
  return { ...dataRoom, documents: [], sale_keys: saleKeys };
}

export function isDataRoomEmpty(dataRoom: SaleDataRoom): boolean {
  return dataRoom.documents.length === 0 && dataRoom.sale_keys.length === 0;
}

export const APPROVE_SALE_FALLBACK_COPY = "Couldn't approve this sale. Try again.";

const APPROVE_SALE_REFUSAL_COPY: Record<string, string> = {
  'This sale is still being built. Approve it once its creation job has completed.':
    "This sale is still being built. You can approve it once it's ready.",
  'This sale failed, so it cannot be approved.':
    "This sale failed to build, so it can't be approved.",
  "This sale's access cut-off or first-round deadline is too soon to open it for bidders.":
    "This sale's access cut-off or round 1 deadline is too close to open it to bidders.",
  'Someone else has already approved this sale.': 'Someone else already approved this sale.',
};

export const ALREADY_APPROVED_MESSAGE = 'Someone else has already approved this sale.';

export function approveSaleRefusalCopy(err: unknown): string {
  const message = firstGraphQLError(err)?.message;
  return (message && APPROVE_SALE_REFUSAL_COPY[message]) ?? APPROVE_SALE_FALLBACK_COPY;
}

export function isAlreadyApproved(err: unknown): boolean {
  return firstGraphQLError(err)?.message === ALREADY_APPROVED_MESSAGE;
}
